/**
 * Turn backend / snake_case copy into user-facing labels and sentences.
 * Prefer known phrase rewrites; always expand snake_case field names.
 */

/** Canonical user-facing names for API / schema field identifiers. */
export const API_FIELD_USER_LABELS: Record<string, string> = {
  asset_class: "Asset class",
  traded_asset_classes: "Asset classes",
  allowed_asset_classes: "Allowed asset classes",
  suggested_asset_class: "Suggested asset class",
  valid_asset_classes: "Valid asset classes",
  broker_account_code: "Broker account",
  account_code: "Account",
  broker_name: "Broker",
  market_data_broker: "Market data broker",
  md_listing_key: "Market data listing",
  instrument_id: "Instrument",
  resolved_symbol: "Resolved symbol",
  tradingview_symbol: "TradingView symbol",
  pinescript: "Pinescript",
  pine_source: "Pinescript",
  policy_version_id: "Policy version",
  policy_tree: "Policy",
  backtest_id: "Simulation",
  report_id: "Report",
  workspace_id: "Workspace",
  parent_node_id: "Parent node",
  binance_product: "Binance product",
  oanda_environment: "OANDA environment",
  consent: "Consent",
  symbol: "Symbol",
};

const PHRASE_REWRITES: Array<{ pattern: RegExp; replace: string }> = [
  {
    pattern:
      /symbol not in registry[,;]?\s*provide\s+asset[_\s-]?class\s+to\s+route\s+market\s+data/gi,
    replace:
      "This symbol isn't set up in Tessera yet. Choose an Asset class so we can try to locate and map it for you.",
  },
  {
    pattern: /\bnot in registry\b/gi,
    replace: "isn't set up in Tessera yet",
  },
  {
    pattern: /\bto route market data\b/gi,
    replace: "so we can load market data",
  },
  {
    pattern: /\bprovide\s+(asset class)\s+to\b/gi,
    replace: "choose an $1 so we can",
  },
  {
    pattern: /\bmarket_data_asset_class_required\b/gi,
    replace: "Asset class confirmation required",
  },
  {
    pattern: /\binstrument_not_registered\b/gi,
    replace: "Symbol isn't set up in Tessera yet",
  },
];

/** Title-case a snake_case (or kebab) identifier when no explicit label exists. */
export function humanizeApiFieldName(field: string): string {
  const key = field.trim();
  if (!key) return key;
  const normalized = key.replace(/-/g, "_").toLowerCase();
  if (API_FIELD_USER_LABELS[normalized]) return API_FIELD_USER_LABELS[normalized];
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part, index) => {
      if (index > 0 && /^(id|url|api|ohlc|pnl|fx|md)$/i.test(part)) {
        return part.toUpperCase();
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

/**
 * Expand snake_case tokens and rewrite known BE phrases for display.
 * Safe for toast titles, inline alerts, and secondary action copy.
 */
export function humanizeApiUserCopy(text: string): string {
  let s = text.trim();
  if (!s) return s;

  for (const { pattern, replace } of PHRASE_REWRITES) {
    s = s.replace(pattern, replace);
  }

  // Expand remaining snake_case identifiers (asset_class → Asset class).
  s = s.replace(/\b([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\b/g, (match) => humanizeApiFieldName(match));

  s = s.replace(/\s*;\s*/g, ". ");
  s = s.replace(/\s{2,}/g, " ").trim();

  // Capitalize sentence starts after phrase rewrites.
  s = s.replace(/(^|[.!?]\s+)([a-z])/g, (_, boundary: string, ch: string) => boundary + ch.toUpperCase());

  return s;
}
